import type {
  IGameState,
  IMoveResult,
  TAIDifficulty,
  TGameMode,
  TMoveError,
  TPlayer,
} from './types';
import { createEmptyBoard } from './utils/create-board';
import { checkWinner } from './utils/check-winner';
import { checkDraw } from './utils/check-draw';

export class TicTacToeGame {
  private state: IGameState;

  constructor(mode: TGameMode = 'local', aiDifficulty?: TAIDifficulty) {
    this.state = {
      board: createEmptyBoard(),
      currentPlayer: 0,
      status: 'idle',
      winner: null,
      winningLine: null,
      mode,
      aiDifficulty,
    };
  }

  start(): void {
    this.state.status = 'playing';
  }

  makeMove(player: TPlayer, row: number, col: number): IMoveResult {
    if (this.state.status !== 'playing') {
      return this.createMoveResult(false, 'not_playing');
    }

    if (player !== this.state.currentPlayer) {
      return this.createMoveResult(false, 'not_your_turn');
    }

    if (this.state.board[row][col] !== null) {
      return this.createMoveResult(false, 'cell_occupied');
    }

    this.state.board[row][col] = player;

    const winnerCheck = checkWinner(this.state.board);
    if (winnerCheck.hasWinner) {
      this.state.status = 'finished';
      this.state.winner = winnerCheck.winner;
      this.state.winningLine = winnerCheck.winningLine;
      return this.createMoveResult(true, null, true, winnerCheck.winner!);
    }

    if (checkDraw(this.state.board)) {
      this.state.status = 'draw';
      return this.createMoveResult(true, null, true, null, true);
    }

    this.switchPlayer();
    return this.createMoveResult(true, null);
  }

  getState(): IGameState {
    return {
      ...this.state,
      board: this.state.board.map((row) => [...row]),
    };
  }

  reset(mode?: TGameMode, aiDifficulty?: TAIDifficulty): void {
    this.state = {
      board: createEmptyBoard(),
      currentPlayer: 0,
      status: 'idle',
      winner: null,
      winningLine: null,
      mode: mode ?? this.state.mode,
      aiDifficulty: aiDifficulty ?? this.state.aiDifficulty,
    };
  }

  private switchPlayer(): void {
    this.state.currentPlayer = this.state.currentPlayer === 0 ? 1 : 0;
  }

  private createMoveResult(
    success: boolean,
    error: TMoveError = null,
    isGameOver: boolean = false,
    winner: TPlayer | null = null,
    isDraw: boolean = false,
  ): IMoveResult {
    return {
      success,
      error,
      gameState: this.getState(),
      isGameOver,
      winner,
      isDraw,
    };
  }
}
